import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyInviteToken, signupWithInvite, verifyRecoveryToken, resetPasswordWithToken } from '../../apiClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { ShieldCheck, Eye, EyeOff, CheckCircle, XCircle, Loader, KeyRound } from 'lucide-react';

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [flowType, setFlowType] = useState<'invite' | 'recovery' | null>(null);
  
  // Get URL parameters - support both Supabase format and our internal format
  const accessToken = searchParams.get('access_token');
  const token = accessToken || searchParams.get('token'); // Use access_token if available, fallback to token
  const type = searchParams.get('type');
  const redirectTo = searchParams.get('redirect_to');

  // Verify the token when component mounts
  useEffect(() => {
    const verifyToken = async () => {
      // Debug logs
      console.log('Token verification params:', {
        accessToken,
        token,
        type,
        allParams: Object.fromEntries(searchParams.entries())
      });

      if (!token) {
        setError('Invalid link. Please check your email for the correct link.');
        setIsVerifyingToken(false);
        return;
      }

      // For Supabase recovery flow
      if (accessToken) {
        setFlowType('recovery');
        setTokenValid(true);
        setEmail(searchParams.get('email') || '');
        setSuccess('Recovery link verified! Please set your new password.');
        setIsVerifyingToken(false);
        return;
      }

      try {
        let result;
        if (type === 'invite') {
          result = await verifyInviteToken(token);
          setFlowType('invite');
        } else if (type === 'recovery') {
          result = await verifyRecoveryToken(token);
          setFlowType('recovery');
        } else {
          throw new Error('Invalid token type');
        }
        
        if (result.success) {
          setTokenValid(true);
          setEmail(result.email || '');
          const successMessage = type === 'invite' 
            ? 'Invite verified! Please set your password to complete signup.'
            : 'Recovery link verified! Please set your new password.';
          setSuccess(successMessage);
        } else {
          const errorMessage = type === 'invite'
            ? result.error || 'Unable to verify invite. Please try again or contact support.'
            : result.error || 'Unable to verify recovery link. Please request a new password reset.';
          setError(errorMessage);
          setTokenValid(false);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setError('An unexpected error occurred. Please try again.');
        setTokenValid(false);
      } finally {
        setIsVerifyingToken(false);
      }
    };

    verifyToken();
  }, [token, type]);

  // Password validation
  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!tokenValid) {
      setError('Invalid invite token. Please request a new invitation.');
      return;
    }

    setIsLoading(true);

    try {
      if (!token) {
        throw new Error('Missing token');
      }

      let result;
      if (flowType === 'invite') {
        result = await signupWithInvite(token, password);
      } else if (flowType === 'recovery') {
        // For both access_token and recovery token cases, use resetPasswordWithToken
        result = await resetPasswordWithToken(token, password);
      } else {
        throw new Error('Invalid flow type');
      }

      if (!result.success) {
        throw new Error(result.error || 'Operation failed');
      }

      const successMessage = flowType === 'invite' 
        ? 'Account created successfully! Redirecting...'
        : 'Password reset successfully! Redirecting...';
      setSuccess(successMessage);
      
      // Redirect after a brief delay
      setTimeout(() => {
        navigate(redirectTo || '/');
      }, 2000);

    } catch (err: any) {
      console.error('Operation error:', err);
      const errorMessage = flowType === 'invite'
        ? err.message || 'Failed to create account. Please try again.'
        : err.message || 'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while verifying token
  if (isVerifyingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cool-gray-100 to-blue-50 flex items-center justify-center px-4">
        <Card className="credential-capital-card w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <Loader className="h-8 w-8 animate-spin text-electric-steel" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-charcoal-ink">Verifying Link</h3>
                <p className="text-sm text-warm-slate">Please wait while we verify your link...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cool-gray-100 to-blue-50 py-8 px-4">
      <div className="w-full max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-midnight-indigo p-3 rounded-2xl credential-capital-shadow-lg">
              {flowType === 'recovery' ? (
                <KeyRound className="h-10 w-10 text-white" />
              ) : (
                <ShieldCheck className="h-10 w-10 text-white" />
              )}
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold text-midnight-indigo mb-2">
            {flowType === 'recovery' ? 'Reset Your Password' : 'Complete Your Signup'}
          </h1>
          <p className="text-warm-slate">
            {flowType === 'recovery' 
              ? 'Set a new password for your account'
              : 'Set your password to access Credential Capital'
            }
          </p>
        </div>

        {/* Signup Form */}
        <Card className="credential-capital-card">
          <CardHeader className="text-center pb-3">
            <CardTitle className="text-xl font-display text-charcoal-ink">
              {flowType === 'recovery' ? 'Set New Password' : 'Create Your Account'}
            </CardTitle>
            {email && (
              <CardDescription className="text-warm-slate text-sm">
                {flowType === 'recovery' ? 'Resetting password for: ' : 'Setting up account for: '}
                <span className="font-semibold">{email}</span>
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status Messages */}
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-verified-green" />
                <AlertDescription className="text-green-800">
                  {success}
                </AlertDescription>
              </Alert>
            )}

            {tokenValid ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-charcoal-ink font-medium text-sm">
                    {flowType === 'recovery' ? 'New Password' : 'Password'}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={flowType === 'recovery' ? 'Enter your new password' : 'Create a strong password'}
                      className="credential-capital-input h-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-warm-slate hover:text-charcoal-ink transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-charcoal-ink font-medium text-sm">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      className="credential-capital-input h-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-warm-slate hover:text-charcoal-ink transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-charcoal-ink mb-2">Password Requirements:</h4>
                  <ul className="text-xs text-warm-slate space-y-1">
                    <li className={`flex items-center ${password.length >= 8 ? 'text-verified-green' : ''}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${password.length >= 8 ? 'bg-verified-green' : 'bg-gray-300'}`} />
                      At least 8 characters
                    </li>
                    <li className={`flex items-center ${/(?=.*[a-z])/.test(password) ? 'text-verified-green' : ''}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/(?=.*[a-z])/.test(password) ? 'bg-verified-green' : 'bg-gray-300'}`} />
                      One lowercase letter
                    </li>
                    <li className={`flex items-center ${/(?=.*[A-Z])/.test(password) ? 'text-verified-green' : ''}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/(?=.*[A-Z])/.test(password) ? 'bg-verified-green' : 'bg-gray-300'}`} />
                      One uppercase letter
                    </li>
                    <li className={`flex items-center ${/(?=.*\d)/.test(password) ? 'text-verified-green' : ''}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/(?=.*\d)/.test(password) ? 'bg-verified-green' : 'bg-gray-300'}`} />
                      One number
                    </li>
                  </ul>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-10 credential-capital-button-primary text-sm font-medium"
                  disabled={isLoading || !password || !confirmPassword}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {flowType === 'recovery' ? 'Resetting Password...' : 'Creating Account...'}
                    </div>
                  ) : (
                    flowType === 'recovery' ? 'Reset Password' : 'Complete Signup'
                  )}
                </Button>
              </form>
            ) : (
              <div className="text-center py-8">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-charcoal-ink mb-2">
                  {flowType === 'recovery' ? 'Invalid Recovery Link' : 'Invalid Invitation'}
                </h3>
                <p className="text-sm text-warm-slate mb-4">
                  {flowType === 'recovery' 
                    ? 'This recovery link is invalid or has expired. Please request a new password reset.'
                    : 'This invitation link is invalid or has expired.'
                  }
                </p>
                <Button 
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="text-sm"
                >
                  Back to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-warm-slate">
            Powered by Credential Capital • Secure • Verifiable • Trusted
          </p>
        </div>
      </div>
    </div>
  );
}